import clsx from 'clsx';
import { forwardRef, useMemo } from 'react';

import * as styles from './KeyboardShortcut.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { useContainerColoring } from '../../layouts/container/Container.js';

const COMPONENT_NAME = 'KeyboardShortcut';

// #region Typings
/**
 * Accepted properties for KeyboardShortcut
 * @public
 */
export interface KeyboardShortcutProps
  extends StylingProps,
    DataTestId,
    MaskingProps,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * The list of keys the component should display, specified in the aria-keyshortcuts format.
   */
  keys: string;
  /**
   * The variant that should be used for styling the keys.
   * @defaultValue 'default'
   */
  variant?: 'default' | 'minimal';
  /**
   * Whether the component should be rendered in a disabled state.
   * @defaultValue 'false'
   */
  disabled?: boolean;
}
// #endregion Typings

const keyMappings: Record<string, string> = {
  meta: 'Cmd',
  control: 'Ctrl',
  escape: 'Esc',
  altgraph: 'Option',
  delete: 'Del',
  arrowup: '↑',
  arrowdown: '↓',
  arrowright: '→',
  arrowleft: '←',
  plus: '+',
  minus: '-',
  multiply: '*',
  divide: '/',
};

/** @internal */
export function createKeyArrayFromString(keys: string): string[] {
  return keys
    .split(' ')[0]
    .split('+')
    .filter((key) => key !== '')
    .map(
      (key) =>
        keyMappings[key.toLowerCase()] ??
        `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    );
}

/**
 * The `KeyboardShortcut` component visualizes one or multiple keys to communicate keyboard shortcuts for certain actions.
 * To specify a shortcut, provide the key sequence in the {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-keyshortcuts | aria-keyshortcuts} format.
 * @public
 */
export const KeyboardShortcut = forwardRef<HTMLElement, KeyboardShortcutProps>(
  (props, forwardedRef) => {
    const {
      keys,
      variant = 'default',
      disabled = false,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const keyArray = useMemo(() => createKeyArrayFromString(keys), [keys]);

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const containerColoring = useContainerColoring();
    const onAccent = containerColoring.variant === 'accent';

    return (
      <kbd
        ref={forwardedRef}
        aria-disabled={disabled}
        className={clsx(styles.keyboardShortcutKeyWrapper, consumerClassName)}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        {keyArray.map((key: string, index: number) => (
          <kbd
            key={`${key}-${index}`}
            className={styles.keyboardShortcutKey({
              variant,
              onAccent,
              disabled,
            })}
          >
            {key}
          </kbd>
        ))}
      </kbd>
    );
  },
);

(
  KeyboardShortcut as typeof KeyboardShortcut & { displayName: string }
).displayName = COMPONENT_NAME;
